import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";
import { environment } from "../environments/environment";
import { LogoutComponent } from "./pages/account/logout/logout.component";
// import { LandingComponent } from './pages/Account/landing/landing.component';
import { AuthenticationGuard } from "./authentication/authentication-guard";
import { SubscriptionGuard } from "./authentication/subscription-guard";
import { OnboardingGuard } from "./authentication/onboarding-guard";
import { MobileOnlyGuard } from "./authentication/mobile-only.guard";
import { RegistrationRegionGuard } from "./authentication/registration-region.guard";
import { RegionEntryGuard } from "./authentication/region-entry.guard";
import { LoginComponent } from "./pages/account/login/login.component";
import { PublicLandingGuard } from "./authentication/public-landing.guard";

const loadPublicLanding = () =>
  import("./pages/public-landing/public-landing.component").then((m) => m.PublicLandingComponent);

const routes: Routes = [
  // FLIP-528: public marketing page for browser visitors. canMatch (not canActivate) so that shell
  // users and anyone with a session fall through to the authenticated "" route below unchanged.
  {
    path: "",
    pathMatch: "full",
    canMatch: [PublicLandingGuard],
    loadComponent: loadPublicLanding,
  },
  {
    path: "landing/contact",
    loadComponent: loadPublicLanding,
    data: { page: "contact" },
  },
  {
    path: "",
    canActivate: [AuthenticationGuard, SubscriptionGuard, OnboardingGuard], // enable for authentication + paid subscription + completed onboarding
    children: [
      { path: "", redirectTo: "landing-page", pathMatch: "full" },
      {
        path: "landing-page",
        loadChildren: () =>
          import("./pages/landing-page/landing-page.module").then(
            (m) => m.LandingPageModule
          ),
      },      
      {
        path: "pace-old",
        loadChildren: () =>
          import("./pages/home/home.module").then((m) => m.HomeModule),
      },
      {
        path: "plan-home",
        loadChildren: () =>
          import("./pages/plan-home/plan-home.module").then(
            (m) => m.PlanHomeModule
          ),
      },
      {
        path: "plan",
        loadChildren: () =>
          import("./pages/plan/plan.module").then((m) => m.PlanModule),        
      },
      {
        path: "plan-old",
        loadChildren: () =>
          import("./pages/plan-old/plan.module").then((m) => m.PlanOldModule),
      },
      {
        path: "score",
        loadChildren: () =>
          import("./pages/score/score.module").then((m) => m.ScoreModule),
      },
      {
        path: "settings",
        loadChildren: () =>
          import("./pages/settings/settings.module").then(
            (m) => m.SettingsModule
          ),
      },

      // { path: 'plan/:planid', component: PlanComponent },
      {
        path: "history",
        loadChildren: () =>
          import("./pages/history/history.module").then((m) => m.HistoryModule),
      },
      // { path: 'referral', component: ReferralComponent },
      {
        path: "brainbites",
        loadChildren: () =>
          import("./pages/brainbites/brainbites.module").then(
            (m) => m.BrainbitesModule
          ),
      },

      // { path: "landing-page", component: LandingPageComponent },
      {
        path: "forms",
        loadChildren: () =>
          import("./pages/forms/forms/forms.module").then((m) => m.FormsModule),
      },
      {
        path: "learn",
        loadChildren: () =>
          import("./pages/learn/learn.module").then((m) => m.LearnModule),
      },
      {
        path: "help",
        loadChildren: () =>
          import("./pages/help/help.module").then((m) => m.HelpModule),
      },
      {
        path: "contact",
        loadChildren: () =>
          import("./pages/contact/contact-routing.module").then(
            (m) => m.ContactRoutingModule
          ),
      },
      {
        path: "sanctuary",
        loadChildren: () =>
          import("./pages/sanctuary/sanctuary-page.module").then(
            (m) => m.SanctuaryPageModule
          ),
      },
      {
        path: "card-library",
        loadChildren: () =>
          import("./pages/card-library/card-library.module").then(
            (m) => m.CardLibraryModule
          ),
      },
      {
        path: "community",
        loadChildren: () =>
          import("./pages/community/community.module").then(
            (m) => m.CommunityModule
          ),
      },
    ],
  },
  // { path: 'landing', component: LandingComponent },
  {
    path: "login",
    component: LoginComponent,
  },
  // Dev-only playground for new-UI (BCAD-529) components awaiting page
  // integration; see src/app/pages/test/. Excluded from production builds.
  ...(environment.production
    ? []
    : [
        {
          path: "test",
          loadComponent: () =>
            import("./pages/test/test.component").then((m) => m.TestComponent),
        },
      ]),
  {
    // FLIP-541: where sign-up starts (login's "Create one" lands here); its button goes on to /register.
    path: "welcome",
    // FLIP-584: closed outside the countries flip* is sold in (login's "Create one" is a client-side link
    // the edge redirect never sees).
    canActivate: [RegistrationRegionGuard],
    loadComponent: () =>
      import("./pages/account/welcome/welcome.component").then(
        (m) => m.WelcomeComponent
      ),
  },
  {
    // FLIP-553: where browser sign-up outside Australia and the USA lands. Public, and exempt from the edge
    // redirect rule so it cannot loop.
    path: "region-unavailable",
    loadComponent: () =>
      import("./pages/account/region-unavailable/region-unavailable.component").then(
        (m) => m.RegionUnavailableComponent
      ),
  },
  {
    path: "register",
    // Account creation is mobile-app only - see MobileOnlyGuard.
    // canActivate: [MobileOnlyGuard],
    canActivate: [RegistrationRegionGuard], // FLIP-584
    loadChildren: () =>
      import("./pages/account/register/register.module").then(
        (m) => m.RegisterModule
      ),
  },
  {
    path: "registration-complete",
    loadChildren: () =>
      import(
        "./pages/account/registration-complete/registration-complete.module"
      ).then((m) => m.RegistrationCompleteModule),
  },
  {
    path: "onboarding",
    loadComponent: () =>
      import("./onboarding/onboarding-flow/onboarding-flow.component").then(
        (m) => m.OnboardingFlowComponent
      ),
  },
  {
    path: "stripe-payments",
    loadChildren: () =>
      import("./pages/account/stripe-payments/stripe-payments.module").then(
        (m) => m.StripePaymentsModule
      ),
  },
  {
    // FLIP-545: Android-only Google Play / Stripe choice, entered from /stripe-payments.
    path: "payment-choice",
    loadChildren: () =>
      import("./pages/account/payment-choice/payment-choice.module").then(
        (m) => m.PaymentChoiceModule
      ),
  },
  {
    path: "subscription-expired",
    loadChildren: () =>
      import(
        "./pages/account/subscription-expired/subscription-expired.module"
      ).then((m) => m.SubscriptionExpiredModule),
  },
  {
    path: "connecting-to-stripe",
    loadChildren: () =>
      import(
        "./pages/account/connecting-to-stripe/connecting-to-stripe.module"
      ).then((m) => m.ConnectingToStripeModule),
  },
  {
    path: "payment-success",
    loadChildren: () =>
      import("./pages/account/payment-success/payment-success.module").then(
        (m) => m.PaymentSuccessModule
      ),
  },
  { path: "logout", component: LogoutComponent },
  {
    path: "reset-password",
    loadChildren: () =>
      import("./pages/account/reset-password/reset-password.module").then(
        (m) => m.ResetPasswordModule
      ),
  },
  {
    path: "confirm-email",
    loadChildren: () =>
      import("./pages/account/confirm-email/confirm-email.module").then(
        (m) => m.ConfirmEmailModule
      ),
  },
  { path: "**", redirectTo: "landing-page" },
];

// FLIP-584: one empty-path parent around every route, so RegionEntryGuard sees the first navigation to any
// URL (before the page renders) without each route carrying it. URLs are unchanged.
const routesWithRegionEntry: Routes = [
  { path: "", canActivateChild: [RegionEntryGuard], children: routes },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routesWithRegionEntry, {
      scrollPositionRestoration: "top",
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
